import PropTypes from "prop-types";
import { useTheme } from "styled-components";
import { useEffect, useState } from "react";

import {
  fixedNumberToTwoDecimals,
  getCalendarReturnsFilteredData,
} from "features/Strategy/utils";
import {
  AnnualReturnsContainer,
  NoDataMessage,
} from "features/Strategy/v2/Detail/StrategyDetails.styles";
import { BarChart, ChartTooltip } from "components/Chart";
import { AnnualReturnsChartPlaceholder } from "./AnnualReturnsChartPlaceholder";
import { Title } from "style/components/Title.styles";

export const AnnualReturnsChart = ({
  data = {},
  height = "350px",
  isLoading,
  title = "Annual Returns",
}) => {
  const theme = useTheme();
  const [parsedData, setParsedData] = useState([]);

  useEffect(() => {
    setParsedData(getCalendarReturnsFilteredData(data));
  }, [data]);

  const minY = Math.min(...parsedData.map((d) => d.value));

  if (isLoading) {
    return <AnnualReturnsChartPlaceholder height="290px" />;
  }

  const getExtremeValuesKey = () => {
    let minValue = 0;
    let maxValue = 0;

    parsedData?.forEach((item) => {
      const { value, value2 } = item;

      if (value < minValue) {
        minValue = value;
      }

      if (value2 < minValue) {
        minValue = value2;
      }

      if (value > maxValue) {
        maxValue = value;
      }

      if (value2 > maxValue) {
        maxValue = value2;
      }
    });

    return { minValue, maxValue };
  };

  const { minValue, maxValue } = getExtremeValuesKey();

  const legendProps = {
    payload: [
      {
        id: parsedData[0]?.label,
        type: "square",
        value: `${parsedData[0]?.label}`,
        color: parsedData[0]?.color,
      },
      {
        id: parsedData[0]?.label2,
        type: "square",
        value: `${parsedData[0]?.label2}`,
        color: parsedData[0]?.color2,
      },
    ],
  };

  return (
    <AnnualReturnsContainer>
      <span className="header">
        <Title className="mb-0">{title}</Title>
      </span>
      <div className="divider my-3"></div>
      {parsedData?.length > 0 ? (
        <div style={{ width: "100%", height }}>
          <BarChart
            data={parsedData}
            xAxisProps={{
              dataKey: "name",
              tickCount: 12,
              axisLine: false,
              fontSize: 12,
              stroke: theme.chart.axis.primary,
            }}
            yAxisProps={{
              domain: [minValue, maxValue],
              tickFormatter: fixedNumberToTwoDecimals,
              axisLine: false,
              fontSize: 12,
              stroke: theme.chart.axis.primary,
            }}
            barDataKey="value"
            bar2DataKey="value2"
            barChartProps={{
              barGap: 10,
              barCategoryGap: 8,
            }}
            legendProps={legendProps}
            barProps={{
              barSize: parsedData.length <= 10 ? 40 : 20,
            }}
            tooltipContent={ChartTooltip}
            referenceLineProps={
              minY < 0 && {
                stroke: "rgba(165, 199, 255, 0.3)",
              }
            }
          />
        </div>
      ) : (
        <NoDataMessage>No data to display</NoDataMessage>
      )}
    </AnnualReturnsContainer>
  );
};

AnnualReturnsChart.propTypes = {
  data: PropTypes.object,
  height: PropTypes.string,
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  isPublicStrategy: PropTypes.bool,
  // legendContent: PropTypes.elementType,
};
